export const ColorPalette = [
  "#A93226", // Dark Rose
  "#7D3C98", // Deep Violet
  "#1E8449", // Dark Emerald
  "#21618C", // Steel Blue
  "#B7950B", // Dark Gold
  "#D35400", // Dark Orange
  "#117A65", // Deep Teal
  "#6C3483", // Dark Purple
  "#BA4A00", // Burnt Sienna
  "#0E6655", // Dark Cyan
  "#566573", // Dark Slate
  "#5B2C6F", // Dark Plum
  "#1A5276", // Dark Blue
  "#9A7D0A", // Dark Yellow
  "#515A5A", // Charcoal
  "#148F77", // Teal Green
  "#C0392B", // Crimson
  "#5D6D7E", // Slate Gray
  "#884EA0", // Dark Orchid
  "#138D75", // Dark Aqua
  "#922B21", // Dark Brick
  "#D4AC0D", // Dark Mustard
  "#2874A6", // Ocean Blue
  "#A04000", // Rust
  "#4A235A", // Eggplant
  "#0B5345", // Deep Dark Teal
  "#B8860B", // Dark Goldenrod
  "#1F618D", // Deep Blue
  "#943126", // Dark Coral
  "#2C3E50", // Midnight Blue
  "#196F3D", // Forest Green
  "#8B4513", // Saddle Brown
  "#76448A", // Medium Purple
  "#1B4F72", // Dark Navy
  "#7D6608", // Olive Gold
  "#0E6251", // Dark Green Teal
  "#873600", // Dark Brown Orange
  "#154360", // Navy
  "#1D8348", // Deep Emerald
  "#6E2C00", // Dark Chocolate
  "#78281F", // Deep Maroon
  "#7B7D7D", // Medium Gray
  "#186A3B", // Hunter Green
  "#DAA520", // Goldenrod
  "#4D5656", // Dark Gray Green
  "#8E44AD", // Rich Purple
  "#2E86C1", // Strong Blue
  "#CB4335", // Deep Red
  "#17A589", // Dark Mint
];
const UNIQUE_COLOR_PALETTE = Array.from(
  new Set(ColorPalette.map((color) => color.toUpperCase())),
);

function normalizeColorKey(value = "") {
  return String(value).trim().toLowerCase();
}

function hashStringFNV1a(input) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function hslToHex(h, s, l) {
  const hue = ((h % 360) + 360) % 360;
  const saturation = Math.max(0, Math.min(100, s)) / 100;
  const lightness = Math.max(0, Math.min(100, l)) / 100;

  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const hueSection = hue / 60;
  const x = chroma * (1 - Math.abs((hueSection % 2) - 1));
  const match = lightness - chroma / 2;

  let red = 0;
  let green = 0;
  let blue = 0;

  if (hueSection >= 0 && hueSection < 1) {
    red = chroma;
    green = x;
  } else if (hueSection < 2) {
    red = x;
    green = chroma;
  } else if (hueSection < 3) {
    green = chroma;
    blue = x;
  } else if (hueSection < 4) {
    green = x;
    blue = chroma;
  } else if (hueSection < 5) {
    red = x;
    blue = chroma;
  } else {
    red = chroma;
    blue = x;
  }

  const toHex = (value) =>
    Math.round((value + match) * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
}

function colorFromHash(hash) {
  const hue = hash % 360;
  const saturation = 64 + ((hash >>> 8) % 16); // 64-79
  const lightness = 42 + ((hash >>> 16) % 14); // 42-55
  return hslToHex(hue, saturation, lightness).toUpperCase();
}

function hexToRgb(hexColor) {
  const normalized = String(hexColor || "")
    .replace("#", "")
    .trim();

  if (normalized.length !== 6) {
    return null;
  }

  const value = Number.parseInt(normalized, 16);
  if (!Number.isFinite(value)) {
    return null;
  }

  return {
    red: (value >> 16) & 0xff,
    green: (value >> 8) & 0xff,
    blue: value & 0xff,
  };
}

function colorDistance(colorA, colorB) {
  const rgbA = hexToRgb(colorA);
  const rgbB = hexToRgb(colorB);
  if (!rgbA || !rgbB) {
    return 0;
  }

  const redDelta = rgbA.red - rgbB.red;
  const greenDelta = rgbA.green - rgbB.green;
  const blueDelta = rgbA.blue - rgbB.blue;
  return Math.sqrt(
    redDelta * redDelta + greenDelta * greenDelta + blueDelta * blueDelta,
  );
}

function isDistinctEnough(candidate, usedColors, minimumDistance) {
  for (const usedColor of usedColors) {
    if (colorDistance(candidate, usedColor) < minimumDistance) {
      return false;
    }
  }

  return true;
}

export function getColorByKey(name, seedOffset = 0) {
  const normalized = normalizeColorKey(name);
  if (!normalized) {
    return "#888888";
  }

  const hash = hashStringFNV1a(`${normalized}|${seedOffset}`);
  return colorFromHash(hash);
}

export function getDistinctColorsForKeys(keys = []) {
  const uniqueKeys = Array.from(
    new Set(
      keys
        .filter((key) => key !== null && key !== undefined)
        .map((key) => String(key)),
    ),
  );

  const sortedKeys = [...uniqueKeys].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" }),
  );

  const usedColors = [];
  const result = {};

  for (const key of sortedKeys) {
    const normalized = normalizeColorKey(key);
    const baseHash = hashStringFNV1a(normalized);

    let candidate = colorFromHash(baseHash);
    const probeStep = 0x9e3779b1;
    const maxAttempts = 2048;
    const minimumColorDistance = 72;
    let selected = false;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const probeHash = baseHash + Math.imul(attempt, probeStep);
      candidate = colorFromHash(probeHash);
      if (isDistinctEnough(candidate, usedColors, minimumColorDistance)) {
        selected = true;
        break;
      }
    }

    if (!selected) {
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const probeHash = baseHash + Math.imul(attempt, probeStep);
        candidate = colorFromHash(probeHash);
        if (!usedColors.includes(candidate)) {
          break;
        }
      }
    }

    usedColors.push(candidate);
    result[key] = candidate;
  }

  return result;
}

export function getColor(index, name = "") {
  if (typeof name === "string" && name.trim().length > 0) {
    return getColorByKey(name);
  }

  if (typeof index === "number" && Number.isFinite(index)) {
    const safeIndex = Math.abs(Math.trunc(index));
    return UNIQUE_COLOR_PALETTE[safeIndex % UNIQUE_COLOR_PALETTE.length];
  }

  return "#888888";
}

// export function getColorFromName(name) {
//   return getColor(Math.floor(name.length /1.8));
// }
